import React, { useContext } from "react";
import Button from "@mui/material/Button";
import { ToasterContext } from "../../../components/shared/Toaster/ToasterContext";
import { NotificationType, SERVER_URL } from "../../../constants/global";
import {
  CircularProgress,
  Modal,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  Box,
} from "@mui/material";
import Link from "@mui/material/Link";
import Grid from "@mui/material/GridLegacy";
import { OrderUserType } from "../../../constants/order";
import { useMutationUpdateManyOrder } from "../../../queries/order/hooks/useMutationUpdateManyOrder";
import axios from "axios";
import ChecklistRoundedIcon from "@mui/icons-material/ChecklistRounded";
import PictureAsPdfRoundedIcon from "@mui/icons-material/PictureAsPdfRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import TaskAltRoundedIcon from "@mui/icons-material/TaskAltRounded";
import CancelRoundedIcon from "@mui/icons-material/CancelRounded";
import LabelRoundedIcon from "@mui/icons-material/LabelRounded";
import HourglassTopRoundedIcon from "@mui/icons-material/HourglassTopRounded";

export type CustomLabelKind = "newLabel" | "returnLabel";

interface OrderItemActionProps {
  user?: OrderUserType.buyer | OrderUserType.seller
  item: any;
  refetch?: any;
  refetchLoading?: boolean;
  isAdmin?: boolean;
  isColumn?: boolean;
  /** Render compact icon buttons with tooltips (Sell/Buy history grid). */
  asIcons?: boolean;
  orders?: any[];
}

/** Compact table action — outlined chip-button, readable in dense rows */
export const orderActionButtonSx = {
  textTransform: "none",
  fontFamily: '"Source Sans 3", sans-serif',
  fontWeight: 600,
  fontSize: 12,
  lineHeight: 1,
  letterSpacing: "0.01em",
  color: "#1E293B",
  bgcolor: "#FFFFFF",
  border: "1px solid #E2E8F0",
  borderRadius: "8px",
  boxShadow: "none",
  px: 1.25,
  py: 0.75,
  minWidth: 0,
  minHeight: 30,
  height: 30,
  whiteSpace: "nowrap",
  "& .MuiButton-startIcon": {
    mr: 0.75,
    ml: 0,
    color: "#2563EB",
    "& > *:nth-of-type(1)": { fontSize: 16 },
  },
  "&:hover": {
    bgcolor: "#F8FAFC",
    borderColor: "#CBD5E1",
    boxShadow: "0 1px 2px rgba(15,23,42,0.06)",
  },
  "&:active": {
    bgcolor: "#F1F5F9",
    borderColor: "#94A3B8",
  },
  "&.Mui-disabled": {
    color: "#94A3B8",
    bgcolor: "#F8FAFC",
    borderColor: "#E2E8F0",
    "& .MuiButton-startIcon": { color: "#94A3B8" },
  },
} as const;

/** Emphasized secondary CTA inside a row (e.g. Track) */
export const orderActionButtonPrimarySx = {
  ...orderActionButtonSx,
  color: "#1D4ED8",
  bgcolor: "#EFF6FF",
  borderColor: "#BFDBFE",
  "& .MuiButton-startIcon": {
    ...orderActionButtonSx["& .MuiButton-startIcon"],
    color: "#2563EB",
  },
  "&:hover": {
    bgcolor: "#DBEAFE",
    borderColor: "#93C5FD",
    boxShadow: "0 1px 2px rgba(37,99,235,0.12)",
  },
  "&:active": {
    bgcolor: "#BFDBFE",
    borderColor: "#60A5FA",
  },
} as const;

/** Destructive secondary action — same chrome, red icon only */
export const orderActionButtonDangerSx = {
  ...orderActionButtonSx,
  "& .MuiButton-startIcon": {
    ...orderActionButtonSx["& .MuiButton-startIcon"],
    color: "#DC2626",
  },
  "&:hover": {
    ...orderActionButtonSx["&:hover"],
    "& .MuiButton-startIcon": { color: "#B91C1C" },
  },
} as const;

const ActionIcon = ({
  title,
  label,
  onClick,
  disabled,
  href,
  primary,
  danger,
  children,
}: {
  title: React.ReactNode;
  label?: string;
  onClick?: (event: React.MouseEvent) => void;
  disabled?: boolean;
  href?: string;
  primary?: boolean;
  danger?: boolean;
  children: React.ReactNode;
}) => {
  const handleClick = (event: React.MouseEvent) => {
    onClick?.(event);
    if (href && !event.defaultPrevented) {
      event.stopPropagation();
      window.open(href, "_blank", "noopener,noreferrer");
    }
  };

  const text = label ?? (typeof title === "string" ? title : undefined);
  const buttonSx = danger
    ? orderActionButtonDangerSx
    : primary
      ? orderActionButtonPrimarySx
      : orderActionButtonSx;

  const button = (
    <Button
      size="small"
      variant="outlined"
      disableElevation
      disabled={disabled}
      onClick={handleClick}
      startIcon={children}
      sx={buttonSx}
      aria-label={typeof title === "string" ? title : text}
    >
      {text}
    </Button>
  );

  return (
    <Tooltip title={title} arrow enterDelay={500}>
      {disabled ? <span style={{ display: "inline-flex" }}>{button}</span> : button}
    </Tooltip>
  );
};

export const OrderItemActions: React.FunctionComponent<OrderItemActionProps> = ({
  user = OrderUserType.seller,
  item,
  refetch,
  refetchLoading = false,
  isAdmin = false,
  isColumn = false,
  asIcons = false,
  orders
}) => {
  const { addToast } = useContext(ToasterContext);
  const [updateManyOrder, { loading: updateLoading }] = useMutationUpdateManyOrder();
  const [regeneratingChecklist, setRegeneratingChecklist] = React.useState(false);
  const [cancelConfirmationOpen, setCancelConfirmationOpen] = React.useState(false);
  const [customLabelDialogOpen, setCustomLabelDialogOpen] = React.useState(false);
  const [customLabelKind, setCustomLabelKind] = React.useState<CustomLabelKind>("newLabel");
  const [customLabelGenerating, setCustomLabelGenerating] = React.useState(false);
  /** Persisted PDF links come from DB via loadCustomLabelMeta (not session-only customLabelFileUrl). */
  const [customLabelMeta, setCustomLabelMeta] = React.useState<{
    adminFileName: string | null;
    returnFileName: string | null;
  }>({ adminFileName: null, returnFileName: null });

  const loadCustomLabelMeta = React.useCallback(async () => {
    if (!item?.orderNumber) return;
    try {
      const r = await axios.get(
        `${SERVER_URL}/v2/order/get/orderByOrderNumber?orderNumber=${encodeURIComponent(
          item.orderNumber
        )}`
      );
      const rows = r.data?.data;
      const list = Array.isArray(rows) ? rows : rows ? [rows] : [];
      const row =
        list.find((o: { id?: number }) => o.id === item.id) ?? list[0];
      setCustomLabelMeta({
        adminFileName: row?.AdminLabel?.name ?? null,
        returnFileName: row?.ReturnLabel?.name ?? null,
      });
    } catch {
      setCustomLabelMeta({ adminFileName: null, returnFileName: null });
    }
  }, [item?.orderNumber, item?.id]);

  const handleBuyerConfirm = (event: any) => {
    event.stopPropagation();
    updateManyOrder({
      variables: {
        data: { shippingStatus: { set: 5 } },
        where: {
          orderNumber: {
            equals: item.orderNumber
          },
          trackingNumber: {
            equals: item.trackingNumber
          },
          stripeTransactionId: {
            equals: item.stripeTransactionId
          },
        }
      }
    }).then(() => {
      refetch();
    }).catch((error: any) => {
      addToast({ type: NotificationType.Error, title: error.message });
    })
  }

  const handleAdminCancel = (event: any) => {
    event.stopPropagation();
    setCancelConfirmationOpen(true);
  }

  const handleConfirmCancel = (event: any) => {
    event.stopPropagation();
    setCancelConfirmationOpen(false);
    updateManyOrder({
      variables: {
        data: { status: { set: 0 } },
        where: {
          orderNumber: {
            equals: item.orderNumber
          },
        }
      }
    }).then(() => {
      refetch();
    }).catch((error: any) => {
      addToast({ type: NotificationType.Error, title: error.message });
    })
  }

  const handleCancelConfirmationClose = (event: any) => {
    event.stopPropagation();
    setCancelConfirmationOpen(false);
  }

  const regenerateChecklist = async (orders: any[], trackingNumber: string) => {
    setRegeneratingChecklist(true);

    const orderNumber = orders[0]?.orderNumber;
    const orderDetailsApi = `${SERVER_URL}/v2/order/get/orderByOrderNumber?orderNumber=${orderNumber}`;
    const response = await axios.get(orderDetailsApi);
    const orderDetails = response.data.data[0];
    const shipDate = orderDetails?.shipDate;
    const pickupDate = orderDetails?.pickupDate;

    const api = `${SERVER_URL}/v2/checklist/generate`;
    const postData = {
      orders,
      trackingNumber,
      shipDate: new Date(shipDate).toISOString(),
      pickupDate: new Date(pickupDate).toISOString(),
    };
    axios
      .post(api, postData)
      .then((res) => {
        if (res.data.success) {
          addToast({ type: NotificationType.Success, title: "Checklist regenerated successfully!" });
          refetch();
        } else {
          addToast({ type: NotificationType.Error, title: "Failed to regenerate checklist." });
        }
      })
      .catch((error: any) => {
        console.error("Error regenerating checklist:", error);
        addToast({ type: NotificationType.Error, title: error.message });
      })
      .finally(() => {
        setRegeneratingChecklist(false);
      });
  };
  const openCustomLabelDialog = (event: React.MouseEvent) => {
    event.stopPropagation();
    setCustomLabelKind("newLabel");
    setCustomLabelDialogOpen(true);
  };

  React.useEffect(() => {
    if (customLabelDialogOpen && isAdmin) {
      loadCustomLabelMeta();
    }
  }, [customLabelDialogOpen, isAdmin, loadCustomLabelMeta]);

  const closeCustomLabelDialog = (
    _event?: unknown,
    _reason?: "backdropClick" | "escapeKeyDown"
  ) => {
    setCustomLabelDialogOpen(false);
    setCustomLabelGenerating(false);
  };

  const generateCustomLabel = async (event: React.MouseEvent) => {
    event.stopPropagation();
    setCustomLabelGenerating(true);

    try {
      const api = `${SERVER_URL}/v2/order/admin/generate-custom-label`;
      const res = await axios.post(api, {
        orders: orders ?? [],
        kind: customLabelKind,
      });
      const payload = res.data;
      if (!payload?.success || !payload?.fileName) {
        const errMsg =
          typeof payload?.error === "string"
            ? payload.error
            : payload?.error?.message ?? "Failed to generate label.";
        throw new Error(errMsg);
      }
      addToast({
        type: NotificationType.Success,
        title:
          customLabelKind === "returnLabel"
            ? "Return label generated."
            : "Label generated.",
      });
      await refetch?.();
      await loadCustomLabelMeta();
    } catch (error: any) {
      const msg =
        error?.response?.data?.error ??
        error?.response?.data?.message ??
        error?.message ??
        "Failed to generate label.";
      addToast({
        type: NotificationType.Error,
        title: typeof msg === "string" ? msg : "Failed to generate label.",
      });
    } finally {
      setCustomLabelGenerating(false);
    }
  };

  const selectedCustomLabelFileName =
    customLabelKind === "newLabel"
      ? customLabelMeta.adminFileName
      : customLabelMeta.returnFileName;

  const selectedCustomLabelUrl = selectedCustomLabelFileName
    ? `${SERVER_URL}/file/${encodeURIComponent(selectedCustomLabelFileName)}`
    : null;

  const handlePrintChecklist = (event: any) => {
    event.stopPropagation();
    const checklistUrl = `${SERVER_URL}/file/order-checklist-${item.orderNumber}.pdf`;
    axios.head(checklistUrl)
      .then((response) => {
        window.open(checklistUrl, '_blank');
      }).catch((error) => {
        console.error("Error fetching checklist:", error);
        addToast({ type: NotificationType.Error, title: "Checklist not found. Please regenerate it." });
      });
  }

  return (
    <>
      <Modal open={regeneratingChecklist}>
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          backdropFilter: 'blur(6px)', // <- Blur effect
          backgroundColor: 'rgba(255, 255, 255, 0.1)', // <- Half-transparent dark overlay
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1300, // Ensure it's above most elements
        }}>
          <CircularProgress />
        </div>
      </Modal>

      {asIcons ? (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "nowrap", py: 0.25, minWidth: 0, overflow: "hidden" }}>
          {item.status === 1 && item.shippingStatus === 1 && !item.trackingNumber && !(item.Label && item.Label.id) && (
            <ActionIcon
              label="Preparing…"
              title={
                <>
                  <div>We're generating your label — please wait.</div>
                  <div>For assistance, contact support@primepostrx.com</div>
                </>
              }
              disabled
            >
              <HourglassTopRoundedIcon sx={{ fontSize: 16 }} />
            </ActionIcon>
          )}
          {isAdmin && (item.shippingStatus > 0 && item.orderNumber && !(item.status === 1 && item.shippingStatus === 1 && !item.trackingNumber && !(item.Label && item.Label.id))) && (
            <ActionIcon
              title="Regenerate Checklist"
              onClick={(event) => {
                event.stopPropagation();
                regenerateChecklist(orders ?? [], item.trackingNumber);
              }}
            >
              <RefreshRoundedIcon sx={{ fontSize: 16 }} />
            </ActionIcon>
          )}
          {item.shippingStatus > 0 && item.orderNumber && !(item.status === 1 && item.shippingStatus === 1 && !item.trackingNumber && !(item.Label && item.Label.id)) && (
            <ActionIcon title="Print Checklist" label="Checklist" onClick={handlePrintChecklist}>
              <ChecklistRoundedIcon sx={{ fontSize: 16 }} />
            </ActionIcon>
          )}
          {user === OrderUserType.seller && item.shippingStatus > 0 && item.trackingNumber && (
            <ActionIcon
              title="Print Shipping Label"
              label="Shipping Label"
              href={`${SERVER_URL}/file/order-shipping-label-${item.trackingNumber}.pdf`}
              onClick={(event) => event.stopPropagation()}
            >
              <PictureAsPdfRoundedIcon sx={{ fontSize: 16 }} />
            </ActionIcon>
          )}
          {isAdmin && item.shippingStatus > 0 && item.trackingNumber && item.orderNumber && (
            <ActionIcon
              title="Custom Label"
              onClick={(event) => {
                event.stopPropagation();
                openCustomLabelDialog(event);
              }}
            >
              <LabelRoundedIcon sx={{ fontSize: 16 }} />
            </ActionIcon>
          )}
          {!isAdmin && item.shippingStatus === 4 && user === OrderUserType.buyer && (
            <ActionIcon
              title="Confirm Order"
              onClick={(event) => handleBuyerConfirm(event)}
              disabled={updateLoading || refetchLoading}
            >
              {updateLoading ? <CircularProgress size={14} /> : <TaskAltRoundedIcon sx={{ fontSize: 16 }} />}
            </ActionIcon>
          )}
          {item.shippingStatus === 0 && (
            <ActionIcon
              title="Cancel Order"
              onClick={(event) => handleAdminCancel(event)}
              disabled={updateLoading || refetchLoading}
              danger
            >
              {updateLoading ? <CircularProgress size={14} /> : <CancelRoundedIcon sx={{ fontSize: 16 }} />}
            </ActionIcon>
          )}
        </Box>
      ) : (
      <Grid
        container
        spacing={2}
        sx={isColumn ?
          { p: 0, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }
          : { p: 2, display: 'flex', justifyContent: 'end' }
        }
      >
        {item.status === 1 && item.shippingStatus === 1 && !item.trackingNumber && !(item.Label && item.Label.id) && (
          <Grid item xs={12} md={isColumn ? 12 : 4} xl={isColumn ? 12 : 2}>
            <Tooltip
              title={
                <>
                  <div>We're generating your label — please wait.</div>
                  <div>For assistance, contact support@primepostrx.com</div>
                </>
              }
              arrow
            >
              <span style={{ display: 'block' }}>
                <Button fullWidth sx={{ mr: 2 }} color='primary' variant="contained" disabled>
                  Preparing Shipping Label...
                </Button>
              </span>
            </Tooltip>
          </Grid>
        )}
        {isAdmin && (item.shippingStatus > 0 && item.orderNumber && !(item.status === 1 && item.shippingStatus === 1 && !item.trackingNumber && !(item.Label && item.Label.id))) && (
          <Grid item xs={12} md={isColumn ? 12 : 4} xl={isColumn ? 12 : 2}>
            <Button fullWidth sx={{ mr: 2 }} color='primary' variant="contained" onClick={(event) => {
              event.stopPropagation();
              regenerateChecklist(orders ?? [], item.trackingNumber);
            }}>
              Regenerate Checklist
            </Button>
          </Grid>
        )}
        {item.shippingStatus > 0 && item.orderNumber && !(item.status === 1 && item.shippingStatus === 1 && !item.trackingNumber && !(item.Label && item.Label.id)) && (
          <Grid item xs={12} md={isColumn ? 12 : 4} xl={isColumn ? 12 : 2}>
            <Link>
              <Button fullWidth sx={{ mr: 2 }} color='primary' variant="contained" onClick={handlePrintChecklist}>
                Checklist
              </Button>
            </Link>
          </Grid>
        )}
        {user === OrderUserType.seller && item.shippingStatus > 0 && item.trackingNumber && (
          <Grid item xs={12} md={isColumn ? 12 : 4} xl={isColumn ? 12 : 2}>
            <Link href={`${SERVER_URL}/file/order-shipping-label-${item.trackingNumber}.pdf`} target="_blank">
              <Button fullWidth sx={{ mr: 2 }} color='primary' variant="contained" onClick={(event: any) => event.stopPropagation()}>
                Shipping Label
              </Button>
            </Link>
          </Grid>
        )}
        {isAdmin && item.shippingStatus > 0 && item.trackingNumber && item.orderNumber && (
          <Grid item xs={12} md={isColumn ? 12 : 4} xl={isColumn ? 12 : 2}>
            <Button
              fullWidth
              sx={{ mr: 2 }}
              color="primary"
              variant="contained"
              onMouseDown={(e) => {
                e.stopPropagation();
              }}
              onClick={openCustomLabelDialog}
            >
              Custom label
            </Button>
          </Grid>
        )}
        {!isAdmin && item.shippingStatus === 4 && user === OrderUserType.buyer && (
          <Grid item xs={12} md={4} xl={2}>
            <Button
              sx={{ mr: 2 }}
              color='primary'
              variant="contained"
              onClick={(event: any) => handleBuyerConfirm(event)}
              fullWidth
              disabled={updateLoading || refetchLoading}
            >
              Confirm Order
              {updateLoading && <CircularProgress sx={{ ml: 2 }} size={14} color='inherit' />}
            </Button>
          </Grid>
        )}
        {item.shippingStatus === 0 && (
          <Grid item xs={12} md={isColumn ? 12 : 4} xl={isColumn ? 12 : 2}>
            <Button
              sx={{ ...orderActionButtonDangerSx, mr: 2, width: "100%", justifyContent: "center" }}
              variant="outlined"
              disableElevation
              onClick={(event: any) => handleAdminCancel(event)}
              fullWidth
              disabled={updateLoading || refetchLoading}
              startIcon={
                updateLoading ? (
                  <CircularProgress size={14} />
                ) : (
                  <CancelRoundedIcon sx={{ fontSize: 16 }} />
                )
              }
            >
              Cancel Order
            </Button>
          </Grid>
        )}
        {item.trackingNumber && (
          <Grid item xs={12} md={isColumn ? 12 : 4} xl={isColumn ? 12 : 2}>
            <Button
              color='primary'
              variant="contained"
              fullWidth
              onClick={(event: any) => {
                event.stopPropagation();
                window.open(`https://www.fedex.com/fedextrack/?trknbr=${item.trackingNumber}`, '_blank');
              }}
            >
              Track Order
            </Button>
          </Grid>
        )}
      </Grid>
      )}

      <Dialog
        open={customLabelDialogOpen}
        onClose={closeCustomLabelDialog}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Custom label</DialogTitle>
        <DialogContent>
          <FormControl component="fieldset" sx={{ width: "100%" }}>
            <RadioGroup
              value={customLabelKind}
              onChange={(e) => {
                e.stopPropagation();
                setCustomLabelKind(e.target.value as CustomLabelKind);
              }}
            >
              <FormControlLabel
                value="newLabel"
                control={<Radio />}
                label="New label"
              />
              <FormControlLabel
                value="returnLabel"
                control={<Radio />}
                label="New return label"
              />
            </RadioGroup>
          </FormControl>
          <Box sx={{ mt: 2 }}>
            <Button
              variant="contained"
              color="primary"
              disabled={customLabelGenerating}
              onClick={generateCustomLabel}
            >
              Generate
              {customLabelGenerating && (
                <CircularProgress size={16} sx={{ ml: 1 }} color="inherit" />
              )}
            </Button>
          </Box>
          {selectedCustomLabelUrl && (
            <Box sx={{ mt: 2 }}>
              <Link
                href={selectedCustomLabelUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
              >
                {customLabelKind === "returnLabel"
                  ? "Open return label (PDF)"
                  : "Open admin label (PDF)"}
              </Link>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            variant="outlined"
            onClick={(e) => {
              e.stopPropagation();
              closeCustomLabelDialog();
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={cancelConfirmationOpen} onClose={handleCancelConfirmationClose}>
        <DialogTitle>Cancel Order</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to cancel order {item.orderNumber}?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleConfirmCancel} variant="contained" color="primary">
            Yes
          </Button>
          <Button 
            onClick={handleCancelConfirmationClose} 
            variant="contained" 
            color="secondary"
            sx={{
              backgroundColor: '#6c757d',
              '&:hover': {
                backgroundColor: '#5a6268',
              },
            }}
          >
            No
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}